import { Button, ButtonMode, ButtonSize, Icon, ICONS, Loader, useGlobalModalContext } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import { uniqBy } from 'lodash';
import { ReactElement, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useSetRecoilState } from 'recoil';
import { toasterState } from '../../../atoms/toaster.atom';
import { ONBOARDING_WATCHLIST_CACHE_KEY, ONBOARDING_WATCHLIST_NAME } from '../../../Constants';
import useFetchOnboardingWatchlist from '../../../hooks/user/useFetchOnboardingWatchlist';
import useSaveWatchlist from '../../../hooks/user/useSaveWatchlist';
import { NotificationMode } from '../../../models/User';
import { OnboardingStepProps } from '../Onboarding';
import AlertSettings from '../shared/AlertSettings';
import styles from './Watchlist.module.scss';
import WatchlistSelector from './WatchlistSelector';
import { MODAL_TYPES } from '../../../components/modal/GlobalModal';

const cx = classNames.bind(styles);

const DEFAULT_ALERT_MODE = NotificationMode.Instant;

function Watchlist({ onContinue, onBack, show }: OnboardingStepProps): ReactElement | null {
  const queryClient = useQueryClient();
  const showToaster = useSetRecoilState(toasterState);
  const [focusIndex, setFocusIndex] = useState<any>();
  const { data: watchlist, isLoading } = useFetchOnboardingWatchlist({ enabled: show });
  const { mutateAsync: saveWatchlist } = useSaveWatchlist();
  const { showModal } = useGlobalModalContext();
  const [alertMode, setAlertMode] = useState(DEFAULT_ALERT_MODE);

  const [companies, setCompanies] = useState<any[]>([{}]);

  useEffect(() => {
    if (!isLoading && watchlist) {
      setCompanies(watchlist.items);
      setAlertMode(watchlist.mode || DEFAULT_ALERT_MODE);
    }
  }, [watchlist, isLoading]);

  const showSelector = (cell, index) => {
    setFocusIndex(index);
  };

  const toggleSelections = (checked) => {
    setCompanies((wl) => wl.map((item) => ({ ...item, selected: item.displayName && checked })));
  };

  const toggleSelection = (e, index) => {
    const { checked } = e.currentTarget;

    setCompanies((wl) => {
      // eslint-disable-next-line no-param-reassign
      wl[index].selected = checked;
      return [...wl];
    });
  };

  const update = (results) => {
    setCompanies((wl) => {
      const index = focusIndex;
      const append = index === companies.length - 1;
      wl.splice(index, 1, ...results);

      const beforeCount = wl.length;
      // eslint-disable-next-line no-param-reassign
      wl = uniqBy(wl, (item) => item.tagId);
      const afterCount = wl.length;

      if (beforeCount !== afterCount) {
        showToaster({
          text: 'Duplicate entries and removed them',
          bold: true,
        });
      }

      if (append) {
        wl.push({});
      }

      return [...wl];
    });
  };

  const hasSelections = () => {
    return companies && !!companies.find((wl) => wl.selected);
  };

  const removeSelected = () => {
    setCompanies((companies) => companies.filter((com) => !com.selected));
  };

  const save = () => {
    if (!companies.length) {
      return Promise.resolve();
    }
    const tags = companies.filter((wl) => wl.tagId).map((wl) => wl.tagId);
    return saveWatchlist(
      {
        id: watchlist?.id,
        watchlist: {
          name: ONBOARDING_WATCHLIST_NAME,
          myCS: true,
          mode: alertMode,
          tags,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([ONBOARDING_WATCHLIST_CACHE_KEY]);
        },
      }
    );
  };

  const handleSkip = () => {
    showModal(MODAL_TYPES.CONFIRM_SKIP_ONBOARDING);
  };

  if (!show) {
    return null;
  }
  if (!isLoading) {
    return (
      <div className={cx('body')}>
        <div className={cx('selection-ctx')}>
          <div className={cx('selection-list-message')}>
            <div className={cx('text')}>Type in the identifier cell to search</div>
            <Button className={cx('remove')} disabled={!hasSelections()} mode={ButtonMode.PLAIN} onClick={() => removeSelected()}>
              <Icon name={ICONS.Delete} /> <span>Remove selected</span>
            </Button>
          </div>
          <div className={cx('table-ctx')}>
            <table className="ui celled table">
              <thead>
                <tr>
                  <th style={{ width: 15 }}>
                    <input type="checkbox" onChange={(e) => toggleSelections(e.currentTarget.checked)} />
                  </th>
                  <th>
                    <div className={cx('table-header')}>Identifier</div>
                  </th>
                  <th>
                    <div className={cx('table-header')}>Issuer</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {companies.map((item, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <tr key={`wl-item-${index}`}>
                    <td>{item.displayName && <input type="checkbox" checked={item.selected} onChange={(e) => toggleSelection(e, index)} />}</td>
                    {focusIndex === index ? (
                      <td className={cx('search-cell')}>
                        <WatchlistSelector onSelect={(resp) => update(resp)} onClose={() => setFocusIndex(null)} />
                      </td>
                    ) : (
                      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                      <td onClick={(e) => showSelector(e.currentTarget, index)}>
                        {item.displayName ? (
                          item.displayTicker || item.displayName
                        ) : (
                          <span>
                            <Icon name={ICONS.Search} width={12} height={12} /> Search ...
                          </span>
                        )}
                      </td>
                    )}
                    <td>{item.displayName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={cx('alerts-ctx')}>
            <AlertSettings selected={alertMode} onChange={(value) => setAlertMode(value)} />
          </div>
        </div>
        <div className={cx('footer')}>
          <div className={cx('contents')}>
            <Button mode={ButtonMode.SECONDARY} size={ButtonSize.MEDIUM} onClick={onBack}>
              Previous
            </Button>
            <div className={cx('spacer')} />
            <Button className={cx('skip-button')} mode={ButtonMode.SECONDARY} size={ButtonSize.MEDIUM} onClick={handleSkip}>
              Skip Onboarding
            </Button>
            <Button mode={ButtonMode.PRIMARY} size={ButtonSize.MEDIUM} onClick={() => onContinue(save)}>
              Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return <Loader className="cs-loader" position="center" />;
}

export default Watchlist;
